import { message, Select, Table, Icon, Button } from "antd";
import * as R from "ramda";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "recompose";
import { dispatchOnMount, spinWhile } from "@worcket/common/hocs";
import moment from "moment";
import { Bar } from "react-chartjs-2";

import Spinner from "../../../components/feedback/Spinner";
import {
  fetchCompanyPurchases,
  FETCH_COMPANY_PURCHASES
} from "../../../store/actions/purchases";
import Page from "../../../components/display/Page";
import { ACCEPT_LANGUAGE } from "../../../locales/acceptedLanguages";

import styled from "styled-components/macro";

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const StyledTable = styled(Table)`
  .column-price {
    text-align: right;
    /* Align prices with total price in footer */
    padding-right: 16px !important;
  }

  .column-date {
    padding-left: 16px !important;
  }

  .ant-table-placeholder {
    padding: 40px 0;
  }

  .ant-table-content {
    ${({ collapsed }) => !collapsed && `display: none;`}
  }

  .ant-table-title {
    ${({ collapsed }) => !collapsed && `border-radius: 4px;`}
  }
`;

const ChartContainer = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  > canvas {
    flex: 1 1 100%;
    margin: 20px 0;
  }
`;

const SelectsContainer = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h1 {
    margin: 0;
  }

  .ant-select {
    margin-left: 10px;
  }
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;

  i {
    font-size: 40px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const { Option } = Select;

const Total = styled.div`
  display: flex;
  justify-content: space-between;

  .total {
    font-weight: bold;
  }
`;

const ENABLE_TEST_PAYMENTS_YEAR = 2019;

const TestPayments = ({ purchases, error }) => {
  const { t, i18n } = useTranslation(["balance"]);

  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [activeYear, setActiveYear] = useState(currentYear);
  const [activeMonth, setActiveMonth] = useState(currentMonth);
  const [tableCollapsed, setTableCollapsed] = useState(false);

  const popInfo = useCallback(() => {
    message.info(t("balance:updateBalance"));
  }, [t]);

  // https://stackoverflow.com/questions/55075604/react-hooks-useeffect-only-on-update
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      popInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMonth]);

  const currentYearPurchases = R.pipe(
    R.propOr({}, activeYear),
    R.map(purchasesList => ({
      purchasesList,
      totalSpent: R.sum(R.pluck("price", purchasesList))
    }))
  )(purchases);

  const selectedMonth = R.propOr({}, activeMonth, currentYearPurchases);

  const yearsRange = R.range(
    ENABLE_TEST_PAYMENTS_YEAR,
    new Date().getFullYear() + 1
  );

  const getTestName = R.cond([
    [
      R.propSatisfies(
        R.flip(R.includes)([
          "test-gh",
          "test-worcket",
          "test-armstrong",
          "test-khor",
          "test-midot"
        ]),
        "article"
      ),
      ({ article, idTest }) =>
        t(
          [
            `opportunity:process.stepTypes.${article}.${idTest}.title`,
            `opportunity:process.test.unnamed`
          ],
          { id: idTest }
        )
    ],
    [R.T, R.prop("article")]
  ]);

  return (
    <Page>
      {error ? (
        <Placeholder data-test="payments-error">
          <Icon type="warning" />
          {t("balance:error")}
        </Placeholder>
      ) : (
        <>
          <ChartContainer>
            <Bar
              height={100}
              key={"chart"}
              getElementAtEvent={e => {
                return (
                  !R.isEmpty(e) &&
                  setActiveMonth(
                    R.dec(
                      moment()
                        .month(e[0]._model.label)
                        .format("M")
                    )
                  )
                );
              }}
              data={{
                labels: R.range(0, 12).map(month =>
                  moment()
                    .month(month)
                    .format("MMMM")
                ),
                datasets: [
                  {
                    label: "u$d",
                    backgroundColor: "#3D64FF82",
                    borderWidth: 1,
                    hoverBackgroundColor: "#3D64FFd9",
                    hoverBorderColor: "#3D64FF",
                    data: R.range(0, 12).map(monthIndex =>
                      R.pipe(
                        R.pathOr(0, [monthIndex, "totalSpent"]),
                        price => price.toFixed(2)
                      )(currentYearPurchases)
                    )
                  }
                ]
              }}
              options={{
                legend: {
                  display: false
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true
                      },
                      scaleLabel: {
                        display: true,
                        labelString: "u$d"
                      }
                    }
                  ]
                }
              }}
            />
          </ChartContainer>
          <TitleContainer>
            <SelectsContainer>
              <span>{t("balance:filterByDate")}</span>
              <Select
                onChange={year => setActiveYear(Number(year))}
                value={activeYear}
              >
                {yearsRange.map(year => (
                  <Option key={year}>{year}</Option>
                ))}
              </Select>
              <Select
                data-test="test-payments-month-select"
                style={{ width: "160px" }}
                onChange={month => setActiveMonth(month)}
                value={activeMonth}
              >
                {R.range(
                  0,
                  activeYear === currentYear ? currentMonth + 1 : 12
                ).map(month => (
                  <Option value={month} key={month}>
                    {moment()
                      .month(month)
                      .format("MMMM")}
                  </Option>
                ))}
              </Select>
            </SelectsContainer>
          </TitleContainer>
          <StyledTable
            data-test="test-payments-table"
            bordered={true}
            style={{ width: "100%" }}
            pagination={
              tableCollapsed
                ? false
                : {
                    hideOnSinglePage: true
                  }
            }
            collapsed={
              !(
                selectedMonth.purchasesList &&
                selectedMonth.purchasesList.length === 0
              ) || tableCollapsed
            }
            locale={{
              emptyText: t("balance:noPayments")
            }}
            {...(selectedMonth.purchasesList &&
            selectedMonth.purchasesList.length
              ? {
                  title: () => (
                    <Total data-test="month-price">
                      <span>Total</span>
                      <span className="total">
                        {`$ ${selectedMonth.totalSpent.toFixed(2)}`}
                      </span>
                    </Total>
                  )
                }
              : {})}
            size="middle"
            rowKey={item =>
              `${item.opportunity.id}-${item.candidate.id}-${getTestName(item)}`
            }
            dataSource={selectedMonth.purchasesList || []}
            columns={
              tableCollapsed
                ? [
                    {
                      title: t("balance:table.date"),
                      className: "column-date",
                      dataIndex: "createdAt",
                      key: "createdAt",
                      width: 160,
                      render: createdAt => moment(createdAt).format("L")
                    },
                    {
                      title: t("balance:table.candidateName"),
                      dataIndex: "candidate.fullname",
                      key: "candidate.fullname",
                      render: fullname => (
                        <span data-test="fullname">{fullname}</span>
                      )
                    },
                    {
                      title: t("balance:table.testName"),
                      key: "article",
                      render: test => (
                        <span data-test="article">{getTestName(test)}</span>
                      )
                    },
                    {
                      title: t("balance:table.price"),
                      className: "column-price",
                      dataIndex: "price",
                      key: "price",
                      width: 200,
                      render: price => (
                        <span data-test="price">{price.toFixed(2)}</span>
                      )
                    }
                  ]
                : []
            }
          />
          {selectedMonth.purchasesList && selectedMonth.purchasesList.length && (
            <Button
              style={{ width: "100%", marginTop: "1em" }}
              onClick={() => setTableCollapsed(!tableCollapsed)}
              icon={tableCollapsed ? "up" : "down"}
              type="link"
              data-test="collapse-table"
            >
              {tableCollapsed
                ? t("balance:table.hideDetail")
                : t("balance:table.showDetail")}
            </Button>
          )}
        </>
      )}
    </Page>
  );
};

const enhancer = compose(
  connect(state => ({
    companyUsers: state.company.users,
    purchases: FETCH_COMPANY_PURCHASES.getResponse(state) || {},
    error: FETCH_COMPANY_PURCHASES.getError(state)
  })),
  dispatchOnMount(({ companyId }) => fetchCompanyPurchases({ companyId })),
  spinWhile(
    FETCH_COMPANY_PURCHASES,
    { initial: true },
    <SpinWrapper>
      <Spinner />
    </SpinWrapper>
  )
);

export default enhancer(TestPayments);
